import type { GameState, Player, PlayerRole, TeamState } from '../useGameStore';
import { DEFAULT_ROSTER } from './constants';

const VALID_PLAYER_ROLES: readonly PlayerRole[] = ['Passeur', 'R4', 'Pointu', 'Central', 'Lib'];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isString(value: unknown): value is string {
  return typeof value === 'string';
}

function isPlayerRole(value: unknown): value is PlayerRole {
  if (typeof value !== 'string') return false;
  return VALID_PLAYER_ROLES.some((role) => role === value);
}

function isPlayer(value: unknown): value is Player {
  if (!isRecord(value)) return false;
  const { id, name, role, number } = value;
  if (typeof name !== 'string') return false;
  if (id !== undefined && typeof id !== 'string') return false;
  if (number !== undefined && typeof number !== 'number') return false;
  return typeof role === 'string';
}

function toPlayerArray(value: unknown, fallback: Player[]): Player[] {
  if (value === undefined) return fallback;
  if (!Array.isArray(value)) return fallback;
  const result: Player[] = [];
  for (const item of value) {
    if (isPlayer(item)) {
      result.push(item);
    }
  }
  return result;
}

function toStringArray(value: unknown): string[] {
  if (value === undefined || value === null) return [];
  if (!Array.isArray(value)) return [];
  return value.filter(isString);
}

function toPlayerRoleArray(value: unknown): PlayerRole[] {
  if (value === undefined || value === null) return [];
  if (!Array.isArray(value)) return [];
  return value.filter(isPlayerRole);
}

function toLibero(value: unknown): string | null {
  if (typeof value === 'string') return value;
  return null;
}

function toPlayerRolesMap(value: unknown): Record<string, PlayerRole> {
  if (!isRecord(value)) return {};
  const result: Record<string, PlayerRole> = {};
  for (const [key, entry] of Object.entries(value)) {
    if (isPlayerRole(entry)) {
      result[key] = entry;
    }
  }
  return result;
}

function buildOpponentPlayerRoles(opponentRoster: Player[]): Record<string, PlayerRole> {
  const map: Record<string, PlayerRole> = {};
  for (const player of opponentRoster) {
    if (isPlayerRole(player.role)) {
      map[player.name] = player.role;
    }
  }
  return map;
}

const LEGACY_V0_KEYS = [
  'roster',
  'court',
  'courtRoles',
  'bench',
  'libero',
  'playerRoles',
  'opponentRoster',
  'opponentCourt',
  'opponentCourtRoles',
  'opponentBench',
  'opponentLibero',
] as const;

function isLegacyV0State(value: Record<string, unknown>): boolean {
  return !('teams' in value);
}

function buildMigratedV0Record(old: Record<string, unknown>): Record<string, unknown> {
  const opponentRoster = toPlayerArray(old.opponentRoster, []);

  const usTeam: TeamState = {
    roster: toPlayerArray(old.roster, DEFAULT_ROSTER),
    court: toStringArray(old.court),
    courtRoles: toPlayerRoleArray(old.courtRoles),
    bench: toStringArray(old.bench),
    libero: toLibero(old.libero),
    playerRoles: toPlayerRolesMap(old.playerRoles),
  };

  const themTeam: TeamState = {
    roster: opponentRoster,
    court: toStringArray(old.opponentCourt),
    courtRoles: toPlayerRoleArray(old.opponentCourtRoles),
    bench: toStringArray(old.opponentBench),
    libero: toLibero(old.opponentLibero),
    playerRoles: buildOpponentPlayerRoles(opponentRoster),
  };

  const migrated: Record<string, unknown> = {
    ...old,
    teams: { us: usTeam, them: themTeam },
  };

  for (const key of LEGACY_V0_KEYS) {
    delete migrated[key];
  }

  return migrated;
}

/**
 * Valide la forme minimale attendue d'un GameState persisté : doit posséder
 * `teams.us` et `teams.them` non nuls. Les autres champs ne sont pas validés
 * statiquement (le store en produit des dizaines) ; Zustand persist garantit
 * la cohérence pour les versions non-zero. Cette assertion suffit à éliminer
 * les casts `as GameState` en aval.
 *
 * Note : on prend `unknown` (et pas `Record<string, unknown>`) pour pouvoir
 * narrower vers `GameState` — la `Record<string, unknown>` input possède une
 * index signature incompatible avec les interfaces fermées comme `GameState`.
 */
function assertIsGameState(value: unknown): asserts value is GameState {
  if (!isRecord(value)) {
    throw new Error('migrateGameState: value must be an object');
  }
  const teams = value.teams;
  if (!isRecord(teams)) {
    throw new Error('migrateGameState: missing or invalid `teams` key');
  }
  if (!isRecord(teams.us) || !isRecord(teams.them)) {
    throw new Error('migrateGameState: teams.us and teams.them must be objects');
  }
}

export function migrateGameState(persisted: unknown, version: number): GameState {
  if (!isRecord(persisted)) {
    throw new Error('migrateGameState: persisted state must be an object');
  }

  if (version === 0 && isLegacyV0State(persisted)) {
    const migrated = buildMigratedV0Record(persisted);
    assertIsGameState(migrated);
    return migrated;
  }

  assertIsGameState(persisted);
  return persisted;
}
